import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Bar } from "react-chartjs-2";
import { IoSpeedometerOutline } from "react-icons/io5";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SalesExecutiveReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [expandedExecutive, setExpandedExecutive] = useState(null);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${buildApiUrl(`/api/report/sales-executive-order-report${
        startDate || endDate ? "?" : ""
      }${startDate ? `startDate=${startDate}` : ""}${
        endDate ? `&endDate=${endDate}` : ""
      }`)}`;
      const response = await axios.get(url);
      if (response.data && response.data.data) {
        setReportData(response.data.data);
      } else {
        setError("Received unexpected data format from the server.");
      }
    } catch (err) {
      setError(
        err.response
          ? `Server error ${err.response.status}: ${
              err.response.data.message || err.message
            }`
          : err.request
          ? "No response from server."
          : `Error: ${err.message}`
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const getChartData = (executive) => ({
    labels: [
      "Pending",
      "Accepted",
      "Rejected",
      "Pending",
      "Part. Disp.",
      "Dispatched",
      "Delivered",
      "Returned",
    ],
    datasets: [
      {
        label: "Status & Delivery",
        data: [
          executive.statusBreakdown.pending,
          executive.statusBreakdown.accepted,
          executive.statusBreakdown.rejected,
          executive.deliveryBreakdown.pending,
          executive.deliveryBreakdown.partialDispatched,
          executive.deliveryBreakdown.dispatched,
          executive.deliveryBreakdown.fullyDelivered,
          executive.deliveryBreakdown.returned,
        ],
        backgroundColor: [
          "#FF6384",
          "#36A2EB",
          "#FFCE56",
          "#4BC0C0",
          "#9966FF",
          "#FF9F40",
          "#C9CBCF",
          "#FF5733",
        ],
        borderWidth: 1,
      },
    ],
  });

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top", labels: { font: { size: 10 } } },
      title: {
        display: true,
        text: "Order Status & Delivery",
        font: { size: 12 },
      },
    },
    scales: {
      y: { beginAtZero: true, ticks: { font: { size: 10 } } },
      x: { ticks: { font: { size: 10 } } },
    },
  };

  const toggleAccordion = (salesExecutiveId) => {
    setExpandedExecutive(
      expandedExecutive === salesExecutiveId ? null : salesExecutiveId
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 rounded-md to-pink-50 p-2">
      <div className="w-full space-y-2">
        <h1 className="text-xl font-bold text-gray-900 text-center">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
            Sales Executive
          </span>{" "}
          Report
        </h1>

        <div className="bg-white p-2 rounded-lg shadow flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700">
              Start
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-1 py-0.5 text-sm rounded border border-gray-200 focus:ring-1 focus:ring-indigo-400 bg-gray-50"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-700">
              End
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-1 py-0.5 text-sm rounded border border-gray-200 focus:ring-1 focus:ring-indigo-400 bg-gray-50"
            />
          </div>
          <button
            onClick={fetchReportData}
            className="self-end px-3 py-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-medium rounded hover:from-indigo-700 hover:to-purple-700 focus:ring-2 focus:ring-indigo-300 flex items-center gap-1 w-full sm:w-fit"
          >
            <IoSpeedometerOutline size={14} />
            Refresh
          </button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600 mx-auto"></div>
            <p className="mt-1 text-xs text-gray-600">Loading...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 p-2 rounded text-center text-xs text-red-600">
            {error}
          </div>
        )}

        {!loading && !error && reportData.length === 0 && (
          <div className="bg-white p-2 rounded shadow text-center text-xs text-gray-600">
            No data available.
          </div>
        )}

        {!loading && !error && reportData.length > 0 && (
          <div className="space-y-2">
            {reportData.map((executive) => (
              <div
                key={executive.salesExecutiveId}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                <div
                  className="p-2 cursor-pointer bg-white flex justify-between items-center"
                  onClick={() => toggleAccordion(executive.salesExecutiveId)}
                >
                  <div className="flex flex-col">
                    <h2 className="text-sm font-bold text-gray-800">
                      {executive.name} ({executive.username})
                    </h2>
                    <p className="text-xs text-gray-600">
                      Orders:{" "}
                      <span className="font-medium">
                        {executive.totalOrders}
                      </span>{" "}
                      | Amount:{" "}
                      <span className="font-medium text-green-600">
                        ₹{executive.totalOrderAmount}
                      </span>
                    </p>
                    <p className="text-xs text-gray-600">
                      Disp.:{" "}
                      <span className="font-medium text-blue-600">
                        {executive.totalDispatchedOrdersClient}
                      </span>{" "}
                      | Pend.:{" "}
                      <span className="font-medium text-orange-600">
                        {executive.totalPendingOrdersClient}
                      </span>{" "}
                      | Ret.:{" "}
                      <span className="font-medium text-red-600">
                        ₹{executive.totalReturnedOrderValue}
                      </span>
                    </p>
                  </div>
                  <span className="text-indigo-600 text-xs font-medium">
                    {expandedExecutive === executive.salesExecutiveId
                      ? "Hide"
                      : "Show"}
                  </span>
                </div>

                {expandedExecutive === executive.salesExecutiveId && (
                  <div className="p-2 grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4">
                    <div className=" bg-gray-50 p-2 rounded shadow-md">
                      <Bar
                        data={getChartData(executive)}
                        options={chartOptions}
                      />
                    </div>

                    {executive.clients.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-800 mb-2">
                          Clients
                        </h3>
                        <div className="overflow-x-auto rounded-md max-h-[40rem] border border-gray-300 shadow-md">
                          <table className="min-w-full divide-y divide-gray-200 text-xs">
                            <thead className="bg-indigo-100 sticky top-0 z-10">
                              <tr>
                                <th className="px-2 py-1 text-left font-medium text-indigo-700 uppercase">
                                  Client
                                </th>
                                <th className="px-2 py-1 text-left font-medium text-indigo-700 uppercase">
                                  Orders
                                </th>
                                <th className="px-2 py-1 text-left font-medium text-indigo-700 uppercase">
                                  Amount
                                </th>
                                <th className="px-2 py-1 text-left font-medium text-indigo-700 uppercase">
                                  Delivery
                                </th>
                                <th className="px-2 py-1 text-left font-medium text-indigo-700 uppercase">
                                  Orders
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {executive.clients.map((client) => (
                                <tr
                                  key={client.clientUsername}
                                  className="hover:bg-gray-50"
                                >
                                  <td className="px-2 py-1 whitespace-nowrap font-medium text-gray-900">
                                    {client.clientName} ({client.clientUsername}
                                    )
                                  </td>
                                  <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                                    {client.totalOrders}
                                  </td>
                                  <td className="px-2 py-1 whitespace-nowrap text-green-600 font-medium">
                                    ₹{client.totalAmount}
                                  </td>
                                  <td className="px-2 py-1 whitespace-nowrap text-gray-600">
                                    P:{client.deliveryStatus.pending}, PD:
                                    {client.deliveryStatus.partialDispatched},
                                    D:{client.deliveryStatus.dispatched}, FD:
                                    {client.deliveryStatus.fullyDelivered}, R:
                                    {client.deliveryStatus.returned}
                                  </td>
                                  <td className="px-2 py-1 text-[10px] text-gray-600">
                                    <ul className="space-y-1">
                                      {client.orders.map((order) => (
                                        <li
                                          key={order.orderId}
                                          className="bg-gray-100 p-1 rounded"
                                        >
                                          <span className="font-medium">
                                            #{order.orderId.slice(-6)}
                                          </span>{" "}
                                          -{" "}
                                          {format(
                                            new Date(order.createdAt),
                                            "MMM dd, yy HH:mm"
                                          )}{" "}
                                          - ₹{order.totalAmount} -
                                          <span className="text-indigo-600">
                                            {order.status}
                                          </span>
                                          (
                                          <span className="text-purple-600">
                                            {order.deliveryStatus}
                                          </span>
                                          )
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesExecutiveReport;
